/**
 * Leitura no WhatsApp, no cabeçalho da conversa:
 * - O selo permanente "Sem marcar como lida": a Central lê o que o monitor
 *   guardou, então abrir aqui não avisa o WhatsApp. A dica explica (passar o
 *   mouse, focar ou tocar).
 * - "Marcar como lida": ação explícita (com confirmação) para quem responde.
 *   Vira um comando que o monitor executa; enquanto isso, "Marcando…".
 */
import React, { useId, useRef, useState } from "react";
import { BsCheck2All } from "react-icons/bs";
import { FiEyeOff } from "react-icons/fi";
import styled from "styled-components";

import ConfirmDialog from "../../../components/ConfirmDialog";
import { MiniSpinner } from "../../../components/LoadingMarcenaria";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { marcarConversaComoLida } from "../../../services/centralProjetos";
import { BotaoCompacto } from "./estilos";

const Ancora = styled.span`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
`;

const Selo = styled.button.attrs({ className: "cp-selo-leitura" })`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 20px;
  padding: 0 7px 0 6px;
  border: 0;
  border-radius: 6px;
  background: var(--surface-2);
  color: var(--ink-2);
  font: inherit;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
  cursor: help;

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  svg {
    flex: 0 0 auto;
  }
`;

const Dica = styled.span`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 30;
  width: 290px;
  max-width: calc(100vw - 32px);
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
  box-shadow: var(--shadow-3);
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.5;
  white-space: normal;
  color: var(--ink-2);

  b {
    display: block;
    margin-bottom: 2px;
    color: var(--ink);
    font-weight: 600;
  }
`;

export function SeloSemMarcar({ podeMarcar }) {
  const [aberta, setAberta] = useState(false);
  const id = useId();
  return (
    <Ancora onMouseEnter={() => setAberta(true)} onMouseLeave={() => setAberta(false)}>
      <Selo
        type="button"
        aria-describedby={aberta ? id : undefined}
        aria-label="Lendo sem marcar como lida"
        // Abrir (não alternar): no computador o mouse já abriu ao passar; no toque, tocar abre e tocar fora fecha.
        onClick={() => setAberta(true)}
        onFocus={() => setAberta(true)}
        onBlur={() => setAberta(false)}
        onKeyDown={(evento) => {
          if (evento.key !== "Escape" || !aberta) return;
          // A dica aberta fica com o ESC; o próximo fecha a conversa.
          evento.preventDefault();
          setAberta(false);
        }}
      >
        <FiEyeOff size={12} aria-hidden="true" />
        <span className="rotulo-selo">Sem marcar como lida</span>
      </Selo>
      {aberta ? (
        <Dica role="tooltip" id={id}>
          <b>Lendo sem marcar como lida</b>
          A Central lê o que o monitor guardou: abrir esta conversa aqui não avisa o WhatsApp, e no celular ela
          continua como não lida.
          {podeMarcar ? " Para marcar no WhatsApp, use o botão Marcar como lida." : ""}
        </Dica>
      ) : null}
    </Ancora>
  );
}

export function BotaoMarcarComoLida({ conversa, leitura, aoPedir }) {
  const { request } = useAuth();
  const toast = useToast();
  const [confirmando, setConfirmando] = useState(false);
  const gatilho = useRef(null);
  const falhou = Boolean(leitura?.falhou);

  async function marcar() {
    try {
      await marcarConversaComoLida(request, conversa.id);
    } catch {
      // O toast do erro já saiu pelo `request`; o diálogo fica aberto para tentar de novo.
      return;
    }
    setConfirmando(false);
    toast.success("Pedido enviado: o WhatsApp marca a conversa como lida em instantes.");
    aoPedir?.();
  }

  let rotulo = "Marcar como lida";
  if (leitura?.marcando) rotulo = "Marcando…";
  else if (falhou) rotulo = "Marcar de novo";
  return (
    <>
      <BotaoCompacto
        ref={gatilho}
        type="button"
        disabled={Boolean(leitura?.marcando)}
        aria-label={leitura?.marcando ? "Marcando a conversa como lida no WhatsApp" : "Marcar a conversa como lida no WhatsApp"}
        title={falhou ? `Não marcou: ${leitura.falhou.erro || "o monitor não confirmou"}` : "Marcar como lida no WhatsApp"}
        onClick={() => setConfirmando(true)}
      >
        {leitura?.marcando ? <MiniSpinner size={14} /> : <BsCheck2All size={17} aria-hidden="true" />}
        <span className="rotulo">{rotulo}</span>
      </BotaoCompacto>
      <ConfirmDialog
        open={confirmando}
        tone="primary"
        titulo="Marcar como lida no WhatsApp?"
        descricao="A conversa deixa de aparecer como não lida no celular da equipe. Ler aqui na Central continua sem marcar nada."
        confirmarLabel="Marcar como lida"
        onConfirmar={marcar}
        onCancelar={() => {
          setConfirmando(false);
          gatilho.current?.focus();
        }}
      />
    </>
  );
}
